import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { CreateJourneyModal } from './modal';
import { CreateJourneyFullModal } from './create-journey-modal';
import { CreateJourneyPanel } from './panel';
import { useHistory } from 'react-router-dom';
import { useNewJourneyStore } from '../store/new-journey';
import { useCurrentOrganization } from 'src/store/organization';
import { trackSegmentEvent } from 'src/utils/segment';
import { Nullable } from 'src/types/nullable.type';
import { CreateJourneyTabType } from './panel-content';
import {
  apiAIGenerateImagePrompt,
  apiAIGenerateImagesStyle2,
  apiAIGenerateOutline,
  apiAIGenerateSection,
  apiAIJourneyFromContent,
  apiAIJourneyFromFile,
  apiAISummarizeWebiste,
  apiIsContentTooLong,
  apiJourneyBrainImage,
  apiAssistantCreateJourney,
} from 'src/utils/journeyApi';
import { uploadFileToS3 } from 'src/common/helpers/file-upload';
import { FormikProps } from 'formik';
import nlp from 'compromise';
import uniq from 'lodash/uniq';
import { generateBlockId } from 'src/editor/content-creation/helpers/generate-block-id';
import { createImageBlockLayout, createTextBlockLayout } from 'src/editor/content-creation/layout-manager';
import { generateKeyBetween } from 'fractional-indexing';
import { convertMarkdownToHTML } from 'src/editor/content-creation/journey-brain/use-journey-brain.hook';
import { Block, ImageBlockContent } from 'src/editor/content-creation/types';

export type CreateJourneyMethodType = Nullable<'blank' | 'import' | 'template' | 'automagic' | 'duplicate' | 'organizationTemplate' | 'assistant'>;

export const CreateJourneyMethodTypes: CreateJourneyMethodType[] = [
  'import',
  'template',
  'automagic',
  'assistant',
  'duplicate',
  'organizationTemplate',
];

interface Props {
  modalOpenRef: MutableRefObject<() => void>;
  modalCloseRef: MutableRefObject<() => void>;
  panelOpenRef: MutableRefObject<(type: CreateJourneyMethodType) => void>;
  panelCloseRef: MutableRefObject<() => void>;
}

const getSegmentEventByTab = (tab: CreateJourneyTabType) => {
  if (tab === 'quickstart') {
    return 'new';
  } else if (tab === 'automagic' || tab === 'assistant') {
    return 'automagic';
  }

  return 'existing';
};

async function analyzeUrls(prompt: string, progressFn: (progress: string) => void) {
  const doc = nlp(prompt);
  progressFn('Analyzing URLs');
  const summaries = Promise.all(
    uniq<string>(doc.urls().out('array')).map(async (url: string) => {
      let summary;
      try {
        const res = await apiAISummarizeWebiste({ url: url });
        summary = res.summary;
      } catch (e) {
        console.error(e);
        summary = null;
      }

      return {
        url,
        summary,
      };
    })
  );
  return (await summaries).filter((item) => item.summary !== null);
}

export async function runWithRetries<T>(fn: () => Promise<T>, retries = 3): Promise<T | undefined> {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (e: any) {
      if (e.code === 'RequestAbortedError') {
        return;
      }
      if (e.code === 'InvalidRequestError') {
        return Promise.reject(e);
      }
      if (i === retries - 1) {
        return Promise.reject({ code: 'ServerOverloaded' });
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
  }
}

async function genSlideText(topic: string, summaries: string[], slideInfo: any) {
  if (slideInfo.text_prompt && slideInfo.text_prompt.length > 0) {
    const slideResult = await runWithRetries(() =>
      apiAIGenerateSection({
        topic,
        prompt: slideInfo.text_prompt,
        title: slideInfo.title,
        urls_summaries: summaries,
      })
    );
    return slideResult.slide;
  } else {
    return null;
  }
}

async function genSlideImages(topic: string, slideInfo: any, imgStyle: string) {
  if (slideInfo.image_prompt && slideInfo.image_prompt.length > 0) {
    const response = await runWithRetries(() =>
      apiJourneyBrainImage('generate_from_prompt', {
        prompt: slideInfo.image_prompt,
        count: 1,
        service: 'dalle',
        style_preset: imgStyle,
      })
    );
    return response.images;
  } else {
    return null;
  }
}

async function genImagePrompt(topic: string, slideInfo: any, text: string) {
  const response = await runWithRetries(() =>
    apiAIGenerateImagePrompt({
      slide_text: text,
      slide_title: slideInfo.title,
    })
  );
  return response['image_prompt'];
}

//this is it
async function generateOutlinePrompts(
  i: string,
  summaries: any,
  progressFn: (s: string) => void,
  setError: (s: string) => void
) {
  progressFn('Thinking about image styles');
  const tm1 = setTimeout(() => {
    progressFn('Starting to write the content');
  }, 15000);

  let slidesInfo, imgStyle: any, outline: any;
  try {
    [outline, imgStyle] = await Promise.all([
      runWithRetries(() => apiAIGenerateOutline({ topic: i, summaries })),
      runWithRetries(() => apiAIGenerateImagesStyle2({ topic: i })),
    ]);
    clearTimeout(tm1);

    progressFn('Thinking of some great images to use');
    let slidesGenerated = 0;

    //main stuff
    slidesInfo = await Promise.all(
      outline.slides.map(async (slideInfo: any) => {
        try {
          const text = await genSlideText(i, summaries, slideInfo);
          const imagePrompt = await genImagePrompt(i, slideInfo, text);
          const images = await genSlideImages(i, { ...slideInfo, image_prompt: imagePrompt }, imgStyle.style);
          slidesGenerated++;
          progressFn(`Generated ${slidesGenerated} out of ${outline.slides.length} sections`);

          const enriched = { ...slideInfo };
          if (imagePrompt) {
            enriched.image_prompt = imagePrompt;
          }
          if (text) {
            enriched.text = text;
          }
          if (images) {
            enriched.images = images;
          }

          return enriched;
        } catch (e) {
          console.log(e);
          return slideInfo;
        }
      })
    );
  } catch (e: any) {
    if (e.code === 'InvalidRequestError') {
      console.error(e);
      setError('Sorry, something went wrong. We are looking into it');
      return;
    } else if (e.code === 'ServerOverloaded') {
      console.error(e);
      setError('Sorry, our server is overloaded. Please, try again later');
      return;
    } else {
      console.error(e);
      setError('Sorry, something went wrong. We are looking into it');
      return;
    }
  }

  progressFn(`Wrapping it up for you`);

  const generatedSections: [{ id: string; title: string; blocks: Block[] }] = [
    {
      id: generateBlockId(),
      title: slidesInfo[0].title,
      blocks: [
        {
          id: generateBlockId(),
          content: {
            type: 'text',
            prompt: '',
            value: `<h1 style="text-align:center">${slidesInfo[0].title}</h1><h2 style="text-align:center">${slidesInfo[0].subtitle}</h2>`,
          },
          layout: createTextBlockLayout('100%'),
          position: generateKeyBetween(null, null),
        },
      ],
    },
  ];

  for (const slideInfo of slidesInfo.slice(1)) {
    const title = convertMarkdownToHTML(slideInfo.title);
    const blocks: Block[] = [];
    blocks.push({
      id: generateBlockId(),
      content: {
        type: 'text',
        prompt: '',
        value: `<h2 style="text-align:center">${title}</h2>`,
      },
      layout: createTextBlockLayout('100%'),
      position: '',
    });

    
    const hasText = slideInfo.text_prompt && slideInfo.text_prompt.length > 0;
    const hasImage = slideInfo.image_prompt && slideInfo.image_prompt.length > 0;

    let textBlock: Nullable<Block> = null;
    if (hasText) {
      textBlock = {
        id: generateBlockId(),
        content: {
          type: 'text',
          horizontalAlignment: 'left',
          prompt: slideInfo.text_prompt,
          value: convertMarkdownToHTML(slideInfo.text).replaceAll('>\n<', '><'),
        },
        layout: createTextBlockLayout('100%'),
        position: '',
      };
    }

    let imageBlock: Nullable<Block> = null;
    if (hasImage) {
      imageBlock = {
        id: generateBlockId(),
        content: {
          type: 'image',
          url: slideInfo.images[0].url,
          objectFit: 'contain',
          dalleParams: {
            prompt: slideInfo.image_prompt,
            urls: slideInfo.images.map((image: any) => image.url),
          },
        },
        layout: createImageBlockLayout('100%', { aspectRatio: null }),
        position: '',
      };
    }

    if (textBlock && imageBlock) {
      if (slideInfo.text.length > 830) {
        imageBlock.layout.spec.width = '100%';
        blocks.push(imageBlock);
        textBlock.layout.spec.width = '100%';
        blocks.push(textBlock);
      } else {
        textBlock.layout.spec.width = '50%';
        blocks.push(textBlock);
        imageBlock.layout.spec.width = '50%';
        (imageBlock.content as ImageBlockContent).objectFit = 'cover';
        blocks.push(imageBlock);
      }
    } else if (textBlock) {
      textBlock.layout.spec.width = '100%';
      blocks.push(textBlock);
    } else if (imageBlock) {
      imageBlock.layout.spec.width = '100%';
      blocks.push(imageBlock);
    }

    let lastPosition = null;
    for (const block of blocks) {
      lastPosition = generateKeyBetween(lastPosition, null);
      block.position = lastPosition;
    }

    generatedSections.push({
      title: slideInfo.title,
      id: generateBlockId(),
      blocks: blocks,
    });

  }

  return { sections: generatedSections, imageStyle: imgStyle.style };
}

// NEW FUNCTION: Helper to format assistant journey payload similar to generateOutlinePrompts()
function formatAssistantJourney(payload: any) {
  const { outline, slidesJson, titleSubtitle } = payload;
  const slides = slidesJson && slidesJson.slides ? slidesJson.slides : [];
  const journeyTitle = typeof titleSubtitle === 'object' ? titleSubtitle.title : titleSubtitle;
  const sections = [];

  if (slides.length > 0) {
    sections.push({
      id: generateBlockId(),
      title: slides[0].title,
      blocks: [
        {
          id: generateBlockId(),
          content: {
            type: 'text',
            prompt: '',
            value: `<h1 style="text-align:center">${slides[0].title}</h1><h2 style="text-align:center">${slides[0].subtitle || ''}</h2>`,
          },
          layout: createTextBlockLayout('100%'),
          position: generateKeyBetween(null, null),
        },
      ],
    });
  }

  for (let i = 1; i < slides.length; i++) {
    const slide = slides[i];
    const titleHTML = convertMarkdownToHTML(slide.title);
    const blocks = [];

    // Title block
    blocks.push({
      id: generateBlockId(),
      content: {
        type: 'text',
        prompt: '',
        value: `<h2 style="text-align:center">${titleHTML}</h2>`,
      },
      layout: createTextBlockLayout('100%'),
      position: '',
    });

    // Text block if available
    if (slide.text) {
      blocks.push({
        id: generateBlockId(),
        content: {
          type: 'text',
          horizontalAlignment: 'left',
          prompt: slide.text_prompt || '',
          value: convertMarkdownToHTML(slide.text),
        },
        layout: createTextBlockLayout('100%'),
        position: '',
      });
    }

    // Image block if available
    if (slide.images && slide.images.length > 0) {
      blocks.push({
        id: generateBlockId(),
        content: {
          type: 'image',
          url: slide.images[0].url,
          objectFit: 'contain',
          dalleParams: {
            prompt: slide.image_prompt || '',
            urls: slide.images.map((image: any) => image.url),
          },
        },
        layout: createImageBlockLayout('100%', { aspectRatio: null }),
        position: '',
      });
    }

    // Assign sequential positions to blocks
    let lastPosition = null;
    for (let j = 0; j < blocks.length; j++) {
      lastPosition = generateKeyBetween(lastPosition, null);
      blocks[j].position = lastPosition;
    }

    sections.push({
      id: generateBlockId(),
      title: slide.title,
      blocks,
    });
  }

  return { sections, imageStyle: outline && outline.imgStyle ? outline.imgStyle : null, journeyTitle };
}

export const CreateJourneyWidget = ({ modalOpenRef, modalCloseRef, panelCloseRef, panelOpenRef }: Props) => {
  const history = useHistory();

  const [animate, setAnimate] = useState(false);
  const [createType, setCreateType] = useState<CreateJourneyMethodType>(null);
  const [isFullModalOpen, setIsFullModalOpen] = useState(false);
  const pageSearchParams = new URLSearchParams(window.location.search);
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const sellerDomain = (currentOrganization.url || '').replaceAll('https://', '').replaceAll('www.', '');
  const createNeueJourney = useNewJourneyStore((state) => state.createNeueJourney);
  const fileUrlRef = useRef('');
  const organizationSlug = currentOrganization.slug;

  const createJourney = (params: object = {}) => {
    createNeueJourney({
      history,
      generatedSlug: null,
      sellerDomain,
      recipientDomain: '',
      journeyToDuplicate: null,
      recipientLogoUrl: null,
      recipientName: '',
      ...params,
    });
  };

  useEffect(() => {
    modalOpenRef.current = () => {
      setIsFullModalOpen(true);
    };
    modalCloseRef.current = () => {
      setIsFullModalOpen(false);
    };
    panelOpenRef.current = (type: CreateJourneyMethodType) => {
      setCreateType(type);
      setAnimate(true);
    };
    panelCloseRef.current = () => {
      setAnimate(false);
      setTimeout(() => {
        setCreateType(null);
        pageSearchParams.delete('create');
        history.replace({
          pathname: window.location.pathname,
          search: pageSearchParams.toString(),
        });
      }, 200);
    };
  }, [pageSearchParams]);

  const generateJourneyFromContent = async (payload: object, isFile: boolean = false): Promise<any> => {
    const generatingPromise = isFile ? () => apiAIJourneyFromFile(payload) : () => apiAIJourneyFromContent(payload);
    const response = await generatingPromise();
    let returnValue = new Promise((resolve) => resolve({ success: true, response }));
    if (response.detail === 'InvalidRequest') {
      // malformed request to openAI, nothing to suggest here
      returnValue = new Promise((resolve) => resolve({ success: false, tryAgain: false, response: null }));
    } else if (response.detail === 'ServiceUnavailable') {
      // openAI is overloaded, try again later
      returnValue = new Promise((resolve) => resolve({ success: false, tryAgain: true, response: null }));
    }
    return returnValue;
  };

  const getContentLengthCheckFn = (callback: (result: boolean) => void) => async (payload: object) => {
    const { is_too_long } = await apiIsContentTooLong(payload);
    callback(is_too_long);
  };

  const onFormSubmit = (tab: CreateJourneyTabType) => {
    return async (formProps: FormikProps<Record<string, any>>) => {
      const { values, setFieldValue, setSubmitting } = formProps;

      const checkContentLength = getContentLengthCheckFn((isTooLong) => {
        if (isTooLong) {
          setFieldValue('info', {
            message: "This is a very long document - it'll see more summarization than others",
          });
        }
      });

      try {
        setFieldValue('error', undefined);
        let precreatedSections = null;
        let name = 'Untitled Journey';
        let data: any = {};
        let aiTopic = '';
        let genRes;
        let duplicateParams = {};
        setSubmitting(true);

        if (tab === 'assistant') {
          trackSegmentEvent('Create Journey Modal Action Clicked', {
            action: 'start_your_journey',
            for: '',
            with_duplicate_journey: false,
            type: getSegmentEventByTab(tab),
            journey_type: 'ai-assistant',
          });
          data['aiType'] = 'assistant';
          aiTopic = values.aiTopic;

          const setLoadingStage = (message: string) => {
            setFieldValue('info', { message });
          };
          const setGenerationError = (message: string) => {
            setFieldValue('error', { message: message, retry: true, reset: false });
          };

          try {
            setLoadingStage('Creating full journey...');
            const resourceKeys = (values.resources || []).map((resource: any) => resource.key).filter(Boolean);
            const res = await apiAssistantCreateJourney({
              topic: aiTopic,
              journeyOption: values.journeyOption,
              resources: values.resources,
              resourceKeys: resourceKeys,
              articleUrl: values.articleUrl,
              imageUse: values.imageUse === undefined ? true : values.imageUse,
              prompt: values.prompt,
              websiteUrls: Array.isArray(values.websiteUrls) ? values.websiteUrls.filter(url => url !== '') : values.websiteUrls
            });
            if (!res || !res.payload) {
              genRes = { success: false, tryAgain: true, reset: false };
            } else {
              // Use the new helper function to format the journey response
              const formattedJourney = formatAssistantJourney(res.payload);
              genRes = { success: true, response: formattedJourney };
              data = { ...data, aiTopic };
            }
          } catch (error) {
            setGenerationError('Error creating journey');
            genRes = { success: false, tryAgain: false, reset: false };
          }
        } else if (tab === 'automagic') {
          trackSegmentEvent('Create Journey Modal Action Clicked', {
            action: 'start_your_journey',
            for: '',
            with_duplicate_journey: false,
            type: getSegmentEventByTab(tab),
            topic: aiTopic,
            journey_type: 'ai-topic-based',
          });
          data['aiType'] = 'topic';

          aiTopic = values.aiTopic;

          const setLoadingStage = (message: string) => {
            setFieldValue('info', { message });
          };

          const setGenerationError = (message: string) => {
            setFieldValue('error', { message: message, retry: true, reset: false });
          };

          const summaries = await analyzeUrls(aiTopic, setLoadingStage);
          const res = await generateOutlinePrompts(aiTopic, summaries, setLoadingStage, setGenerationError);

          if (!res) {
            genRes = { success: false, tryAgain: true, reset: false };
          } else {
            genRes = { success: true, response: { sections: res.sections, journey_title: res.sections[0].title } };
            data = { ...data, aiTopic, aiImageStylePreset: res.imageStyle, aiType: 'topic', aiUrlSummaries: summaries };
          }
        } else if (tab === 'import') {
          if (values.file || fileUrlRef.current) {
            trackSegmentEvent('Create Journey Modal Action Clicked', {
              action: 'start_your_journey',
              for: '',
              with_duplicate_journey: false,
              type: getSegmentEventByTab(tab),
              file: values.file || fileUrlRef.current,
              journey_type: 'ai-converted-from-file',
            });
            data['aiType'] = 'converted-from-file';
            if (values.file) {
              const response = await uploadFileToS3(values.file, 'create-from-content-input-files');
              fileUrlRef.current = response.url;
              await checkContentLength({ file_url: response.url });
              genRes = await generateJourneyFromContent({ file_url: response.url }, true);
            } else if (fileUrlRef.current) {
              await checkContentLength({ file_url: fileUrlRef.current });
              genRes = await generateJourneyFromContent({ file_url: fileUrlRef.current }, true);
            }
          } else if (values.aiTopic) {
            aiTopic = values.aiTopic;
            await checkContentLength({ text: aiTopic });

            trackSegmentEvent('Create Journey Modal Action Clicked', {
              action: 'start_your_journey',
              for: '',
              with_duplicate_journey: false,
              type: getSegmentEventByTab(tab),
              journey_type: 'ai-converted-from-content',
              content: aiTopic,
            });
            data['aiType'] = 'converted-from-content';

            genRes = await generateJourneyFromContent({ content: aiTopic });
          }
        } else if (tab === 'duplicate') {
          duplicateParams = { ...values };
          trackSegmentEvent('Create Journey Modal Action Clicked', {
            action: 'start_your_journey',
            for: '',
            with_duplicate_journey: true,
            type: getSegmentEventByTab(tab),
          });
        } else {
          return;
        }

        if (genRes) {
          if (!genRes.success) {
            let errorMessage = 'Something went wrong, please try again later.';
            if (genRes.tryAgain) {
              errorMessage = 'OpenAI is overloaded at the moment, please try again later';
            }
            setSubmitting(false);
            setFieldValue('error', { message: errorMessage, retry: genRes.tryAgain || false, reset: false });
            return;
          } else {
            const { response } = genRes;
            precreatedSections = response.sections;
            name = tab === 'assistant' ? response.journeyTitle : response.journey_title;
          }
        }

        createJourney({ precreatedSections, name, data, ...duplicateParams });
        fileUrlRef.current = '';
      } catch (err) {
        setSubmitting(false);
        setFieldValue('error', {
          message: 'Something went wrong, please try again later.',
          unhandled: true,
          retry: true,
        });
        console.error(err);
      }
    };
  };

  const onMethodSelect = (type: CreateJourneyMethodType) => {
    if (type === 'blank') {
      createJourney();
      return;
    }
    if (type) {
      pageSearchParams.set('create', type);
    }
    history.push({
      pathname: `/${organizationSlug}/journeys/all`,
      search: pageSearchParams.toString(),
    });
    modalCloseRef.current();
  };

  const onClosePanel = () => {
    panelCloseRef.current();
    modalCloseRef.current();
  };

  return (
    <>
      <CreateJourneyFullModal isOpen={isFullModalOpen} onClose={() => setIsFullModalOpen(false)} onMethodSelect={onMethodSelect} />
      <CreateJourneyPanel animate={animate} type={createType} onSubmit={onFormSubmit} onClose={onClosePanel} />
    </>
  );
};